import { useState, useEffect } from "react";
import {
  useAllPartySupMasts,
  useCreateGeneralAccount,
  useUpdateGeneralAccount,
  useOnePartySupMast,
} from "http-hooks/partysupmast/usepartysupmast";
import useAuth from "hooks/useAuth";
import Input from "components/ui/Input";
import Select from "components/ui/Select";

const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  </div>
);

export default function SetGeneralAccount({ id, onClose }) {
  const { agttp } = useAuth();
  const [accountDetails, setAccountDetails] = useState({
    acc_code: "",
    acc_name: "",
    acctype: "",
    parentgrp: "",
  });
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [selectedParentGroup, setSelectedParentGroup] = useState(null);

  const { isLoading: isLoadingData, data: existingData } =
    useOnePartySupMast(id);

  const { isLoading: isLoadingAccountTypes, data: accountTypes } =
    useAllPartySupMasts({ grplevel: 2, ag_status: 1 });

  // For grplevel = 0 accounts, we need to fetch the parent to get the grandparent (account type)
  const needsParentData = existingData?.grplevel === 0;
  const { isLoading: isLoadingParentData, data: parentData } = 
    useOnePartySupMast(needsParentData ? existingData?.parentID : null);

  const { isLoading: isLoadingParentGroups, data: parentGroups } =
    useAllPartySupMasts({
      grplevel: 1,
      parentID: accountDetails.acctype,
      ag_status: 1,
    });

  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateGeneralAccount();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateGeneralAccount();

  // Load existing data for edit mode
  useEffect(() => {
    if (id && existingData && accountTypes?.data) {
      // For grplevel = 0, we need parent data to determine the account type (grandparent)
      if (existingData.grplevel === 0 && !parentData) {
        return;
      }

      let acctype, parentgrp;

      // Match PHP logic for determining account type
      if (existingData.grplevel === 1) {
        // Account type is the parent
        acctype = existingData.parentID;
        parentgrp = "";
      } else if (existingData.grplevel === 2) {
        // Account type is itself
        acctype = existingData.ps_Id;
        parentgrp = "";
      } else {
        // grplevel = 0, account type is the grandparent (parent's parent)
        if (parentData) {
          acctype = parentData.parentID; // This is the grandparent
          parentgrp = existingData.parentID; // This is the immediate parent
        } else {
          return;
        }
      }

      setAccountDetails({
        acc_code: existingData.ps_Id || "",
        acc_name: existingData.name || "",
        acctype: acctype,
        parentgrp: parentgrp,
      });

      // Set selected account type
      const selectedType = accountTypes.data.find(
        (type) => type.ps_Id === acctype
      );
      setSelectedAccountType(
        selectedType
          ? { value: selectedType.ps_Id, label: selectedType.name }
          : null
      );
    }
  }, [id, existingData, accountTypes, parentData]);

  // Update parent group selection when parent groups are loaded and we have parentgrp
  useEffect(() => {
    if (accountDetails.parentgrp && parentGroups?.data) {
      const selectedParent = parentGroups.data.find(
        (group) => group.ps_Id === accountDetails.parentgrp
      );
      setSelectedParentGroup(
        selectedParent
          ? { value: selectedParent.ps_Id, label: selectedParent.name }
          : null
      );
    }
  }, [accountDetails.parentgrp, parentGroups]);

  // Check if user is a trader (agttp = 1)
  if (agttp !== 1) {
    return (
      <div className="p-6 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Access Restricted
        </h3>
        <p className="text-sm text-gray-600">
          General Account creation is only available for traders.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
        >
          Close
        </button>
      </div>
    );
  }

  // Check if account is system generated
  const isSystemGenerated = existingData?.systemg === 1;

  // Show system generated notice for edit mode
  const SystemGeneratedNotice = () =>
    isSystemGenerated && (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> This A/C is system generated. Cannot be Edited
        </p>
      </div>
    );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountDetails((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAccountTypeChange = (accountType) => {
    setSelectedAccountType(accountType);
    setSelectedParentGroup(null); // Reset parent group when account type changes
    setAccountDetails((prev) => ({
      ...prev,
      acctype: accountType?.value || "",
      parentgrp: "", // Reset parent group
    }));
  };

  const handleParentGroupChange = (parentGroup) => {
    setSelectedParentGroup(parentGroup);
    setAccountDetails((prev) => ({
      ...prev,
      parentgrp: parentGroup?.value || "",
    }));
  };

  // Transform account types data for Select component
  const accountTypeOptions =
    accountTypes?.data?.map((type) => ({
      value: type.ps_Id,
      label: type.name,
    })) || [];

  // Transform parent groups data for Select component
  const parentGroupOptions =
    parentGroups?.data?.map((group) => ({
      value: group.ps_Id,
      label: group.name,
    })) || [];
  const isCashAndBankGroup = selectedParentGroup?.value === "CNB";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        acc_code: accountDetails.acc_code.trim().toUpperCase(),
        acc_name: accountDetails.acc_name.trim(),
        acctype: accountDetails.acctype,
        parentgrp: accountDetails.parentgrp || undefined,
      };

      if (id) {
        await mutateUpdate({ id, data: submitData });
      } else {
        await mutateCreate(submitData);
      }
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
    }
  };

  if (isLoadingAccountTypes || (id && isLoadingData) || (id && needsParentData && isLoadingParentData)) {
    return <FormSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <div className="border-b border-gray-900/10 pb-10">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {id ? "General Accounts Master Edit" : "General Account Master"}
          </h2>

          <SystemGeneratedNotice />

          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="col-span-full">
              <label
                htmlFor="acctype"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Account Type <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                {id ? (
                  // Show as text in edit mode (like PHP)
                  <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    {selectedAccountType?.label || "Loading..."}
                  </div>
                ) : (
                  <Select
                    options={accountTypeOptions}
                    value={selectedAccountType}
                    onChange={handleAccountTypeChange}
                    placeholder="Choose Here"
                    isSearchable
                    required
                  />
                )}
              </div>
            </div>

            {accountDetails.acctype && (
              <div className="col-span-full">
                <label
                  htmlFor="parentgrp"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Parent Group
                </label>
                <div className="mt-2">
                  {isLoadingParentGroups ? (
                    <div className="h-9 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <Select
                      options={parentGroupOptions}
                      value={selectedParentGroup}
                      onChange={handleParentGroupChange}
                      placeholder="None"
                      isSearchable
                      isClearable
                      isDisabled={isSystemGenerated}
                    />
                  )}
                </div>
                {isCashAndBankGroup && (
                  <p className="mt-2 text-sm text-blue-700">
                    Use a six-character code starting with BANK or CASH, such as
                    {" "}BANKAX or CASH01.
                  </p>
                )}
              </div>
            )}

            <div className="col-span-full">
              <Input
                type="text"
                name="acc_code"
                id="acc_code"
                label="Account Code"
                placeholder={isCashAndBankGroup ? "BANKAX or CASH01" : "Account Code"}
                maxLength="6"
                value={accountDetails.acc_code}
                onChange={handleInputChange}
                disabled={isSystemGenerated}
                required
              />
            </div>

            <div className="col-span-full">
              <Input
                type="text"
                name="acc_name"
                id="acc_name"
                label="Account Name"
                placeholder="Account Name"
                maxLength="30"
                value={accountDetails.acc_name}
                onChange={handleInputChange}
                disabled={isSystemGenerated}
                required
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          onClick={onClose}
          disabled={isCreating || isUpdating}
        >
          Cancel
        </button>

        {/* Show save button only for non-system accounts */}
        {!isSystemGenerated && (
          <button
            type="submit"
            disabled={
              isCreating ||
              isUpdating ||
              !accountDetails.acc_code.trim() ||
              !accountDetails.acc_name.trim() ||
              !accountDetails.acctype
            }
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating || isUpdating
              ? id
                ? "Updating..."
                : "Creating..."
              : id
              ? "Update"
              : "Create"}
          </button>
        )}
      </div>
    </form>
  );
}
